import React from "react";
import { Sun, Moon, Navigation, NavigationOff } from "lucide-react";
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

interface SettingsPageProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  isTrackingLocation: boolean;
  onToggleLocationTracking: () => void;
  defaultMapType: string;
  onMapTypeChange: (mapType: string) => void;
  showFilters?: boolean;
}

const SettingsPage: React.FC<SettingsPageProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  onToggleTheme,
  isTrackingLocation,
  onToggleLocationTracking,
  defaultMapType,
  onMapTypeChange,
}) => {
  return (
    <ResponsiveModal open={isOpen} onOpenChange={onClose}>
      <ResponsiveModalContent size="sm" className={cn(
        "max-h-[85vh] overflow-hidden flex flex-col",
        isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white"
      )}>
        <ResponsiveModalHeader className={cn(
          "border-b pb-4",
          isDarkMode ? "border-gray-700" : "border-gray-200"
        )}>
          <ResponsiveModalTitle className={isDarkMode ? "text-white" : ""}>
            Settings
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Appearance */}
          <div>
            <h3 className={cn(
              "text-lg font-medium mb-4",
              isDarkMode ? "text-white" : "text-gray-900"
            )}>
              Appearance
            </h3>
            <div className="space-y-3">
              <button
                onClick={onToggleTheme}
                className={cn(
                  "w-full flex items-center justify-between p-3 rounded-lg border transition-colors",
                  isDarkMode
                    ? "border-gray-600 hover:bg-gray-700"
                    : "border-gray-300 hover:bg-gray-50"
                )}
              >
                <div className="flex items-center gap-3">
                  {isDarkMode
                    ? <Sun size={20} className="text-yellow-400" />
                    : <Moon size={20} className="text-gray-600" />
                  }
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Map Settings */}
          <div>
            <h3 className={cn(
              "text-lg font-medium mb-4",
              isDarkMode ? "text-white" : "text-gray-900"
            )}>
              Map Settings
            </h3>
            <div className="space-y-4">
              {/* Map Type Select */}
              <div>
                <label className={cn(
                  "block text-sm font-medium mb-2",
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                )}>
                  Default Map Type
                </label>
                <Select value={defaultMapType} onValueChange={onMapTypeChange}>
                  <SelectTrigger className={cn(
                    "w-full",
                    isDarkMode && "bg-gray-700 border-gray-600 text-white"
                  )}>
                    <SelectValue placeholder="Select map type" />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? "bg-gray-800 border-gray-700" : ""}>
                    <SelectItem value="roadmap" className={isDarkMode ? "text-white focus:bg-gray-700" : ""}>
                      Roadmap
                    </SelectItem>
                    <SelectItem value="satellite" className={isDarkMode ? "text-white focus:bg-gray-700" : ""}>
                      Satellite
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Location Tracking Switch */}
              <div className={cn(
                "flex items-center justify-between p-3 rounded-lg border",
                isDarkMode ? "border-gray-600" : "border-gray-300"
              )}>
                <div className="flex items-center gap-3">
                  {isTrackingLocation
                    ? <Navigation size={20} className={isDarkMode ? "text-blue-400" : "text-blue-600"} />
                    : <NavigationOff size={20} className={isDarkMode ? "text-gray-400" : "text-gray-500"} />
                  }
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    Location Tracking
                  </span>
                </div>
                <Switch
                  checked={isTrackingLocation}
                  onCheckedChange={onToggleLocationTracking}
                />
              </div>
            </div>
          </div>

          {/* About */}
          <div>
            <h3 className={cn(
              "text-lg font-medium mb-4",
              isDarkMode ? "text-white" : "text-gray-900"
            )}>
              About
            </h3>
            <div className={cn(
              "p-4 rounded-lg",
              isDarkMode ? "bg-gray-700" : "bg-gray-50"
            )}>
              <p className={cn(
                "text-sm mb-2",
                isDarkMode ? "text-gray-300" : "text-gray-600"
              )}>
                <strong>LoppeHub</strong> - Find second-hand shops and flea markets in your area
              </p>
              <p className={cn(
                "text-xs",
                isDarkMode ? "text-gray-400" : "text-gray-500"
              )}>
                Version 1.0.0
              </p>
            </div>
          </div>
        </div>

        <ResponsiveModalFooter className={cn(
          "border-t pt-4",
          isDarkMode ? "border-gray-700" : "border-gray-200"
        )}>
          <Button onClick={onClose} className="w-full">
            Done
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};

export default SettingsPage;
